import { BEIGE_600, WHITE } from 'constants/styling/theme'
import { Box, SxProps, Theme } from '@mui/material'
import { FC, KeyboardEvent, MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent, TouchEvent as ReactTouchEvent, useCallback, useEffect, useRef, useState } from 'react'

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

/**
 * @typedef BeforeAfterSliderVariant Visual variant of the slider.
 * `'default'` — full-size slider with a large handle (Photo Studio preview, onboarding, upsell).
 * `'thumbnail'` — compact slider with a small handle suitable for product catalogue cards.
 */
export type BeforeAfterSliderVariant = 'default' | 'thumbnail'

interface BeforeAfterSliderProps {
  /** URL of the "before" image (shown on the left side of the divider). */
  beforeImage: string
  /** URL of the "after" image (shown on the right side of the divider). */
  afterImage: string
  /** Alt text for the "before" image. Defaults to `''` (decorative — the handle carries the semantic role). */
  beforeAlt?: string
  /** Alt text for the "after" image. Defaults to `''` (decorative — the handle carries the semantic role). */
  afterAlt?: string
  /** Visual variant. Use `'thumbnail'` for small card previews. Defaults to `'default'`. */
  variant?: BeforeAfterSliderVariant
  /** Height of the slider container. Accepts any CSS value. Defaults to `'100%'`. */
  height?: string | number
  /** Initial divider position as a percentage (0–100). Clamped to range. Defaults to `50`. */
  initialPosition?: number
  /** Accessible label applied to the drag handle. Consumers should pass a translated string. */
  handleAriaLabel?: string
  /**
   * The divider follows a hovering mouse instead of needing a drag, and glides back to
   * `initialPosition` when the pointer leaves. Touch keeps the handle drag. Defaults to `false`.
   */
  followPointer?: boolean
  /**
   * Only the handle drags, so a click on the image reaches a parent (a card that opens on click).
   * Otherwise the whole image drags the divider, as in the photo studio. Defaults to `false`.
   */
  handleOnlyDrag?: boolean
  /** Additional MUI sx overrides applied to the outer container. */
  sx?: SxProps<Theme>
}

const VARIANT_STYLES: Record<BeforeAfterSliderVariant, {
  handleSize: string
  iconSize: string
  iconOffset: string
  dividerWidth: string
}> = {
  default: {
    handleSize: '4rem',
    iconSize: '1.6rem',
    iconOffset: '0.4rem',
    dividerWidth: '2px'
  },
  thumbnail: {
    handleSize: '2.4rem',
    iconSize: '1rem',
    iconOffset: '0.2rem',
    dividerWidth: '1px'
  }
}

const clamp = (value: number) => Math.max(0, Math.min(100, value))

/** While following the pointer the handle stays clear of the edges, where the container would clip it. */
const FOLLOW_MIN = 10
const FOLLOW_MAX = 90

const KEYBOARD_STEP = 1
const KEYBOARD_PAGE_STEP = 10

/**
 * Before/After image comparison slider with a draggable divider.
 *
 * Renders two images stacked with a vertical divider that can be dragged (mouse, touch, or keyboard)
 * to compare them. Responsive by default — the divider position is tracked as a percentage,
 * so the slider scales to any container size.
 *
 * Supports two variants: `'default'` for full-size previews (e.g. Photo Studio) and
 * `'thumbnail'` for compact usage in the product catalogue.
 */
export const BeforeAfterSlider: FC<BeforeAfterSliderProps> = ({
  beforeImage,
  afterImage,
  beforeAlt = '',
  afterAlt = '',
  variant = 'default',
  height = '100%',
  initialPosition = 50,
  handleAriaLabel,
  followPointer = false,
  handleOnlyDrag = false,
  sx
}) => {
  const [sliderPosition, setSliderPosition] = useState(() => clamp(initialPosition))
  const [isAnimating, setIsAnimating] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const isDraggingRef = useRef(false)
  const dragCleanupRef = useRef<(() => void) | null>(null)
  const rafIdRef = useRef<number | null>(null)

  const jumpTransition = isAnimating ? 'left 0.25s ease-out, clip-path 0.25s ease-out' : 'none'

  const styles = VARIANT_STYLES[variant]

  useEffect(() => () => {
    if (rafIdRef.current !== null) cancelAnimationFrame(rafIdRef.current)
    dragCleanupRef.current?.()
  }, [])

  const updatePosition = useCallback((clientX: number) => {
    if (rafIdRef.current !== null) return
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null
      if (!containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      if (rect.width === 0) return
      const x = clientX - rect.left
      setSliderPosition(clamp((x / rect.width) * 100))
    })
  }, [])

  const handleMouseDown = useCallback((e: ReactMouseEvent<HTMLDivElement>) => {
    isDraggingRef.current = true
    setIsAnimating(true)
    updatePosition(e.clientX)

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current) return
      setIsAnimating(false)
      e.preventDefault()
      updatePosition(e.clientX)
    }

    const cleanup = () => {
      isDraggingRef.current = false
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', cleanup)
      dragCleanupRef.current = null
    }

    dragCleanupRef.current = cleanup
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', cleanup)
  }, [updatePosition])

  const handleTouchStart = useCallback((e: ReactTouchEvent<HTMLDivElement>) => {
    const startTouch = e.touches[0]
    if (!startTouch) return
    isDraggingRef.current = true
    setIsAnimating(true)
    updatePosition(startTouch.clientX)

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDraggingRef.current) return
      const touch = e.touches[0]
      if (!touch) return
      setIsAnimating(false)
      e.preventDefault()
      updatePosition(touch.clientX)
    }

    const cleanup = () => {
      isDraggingRef.current = false
      document.removeEventListener('touchmove', handleTouchMove)
      document.removeEventListener('touchend', cleanup)
      document.removeEventListener('touchcancel', cleanup)
      dragCleanupRef.current = null
    }

    dragCleanupRef.current = cleanup
    document.addEventListener('touchmove', handleTouchMove, { passive: false })
    document.addEventListener('touchend', cleanup)
    document.addEventListener('touchcancel', cleanup)
  }, [updatePosition])

  const handlePointerFollow = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'touch' || isDraggingRef.current || !containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    if (rect.width === 0) return
    const percent = ((e.clientX - rect.left) / rect.width) * 100
    setIsAnimating(false)
    setSliderPosition(Math.max(FOLLOW_MIN, Math.min(FOLLOW_MAX, percent)))
  }, [])

  const handlePointerLeave = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'touch' || isDraggingRef.current) return
    setIsAnimating(true)
    setSliderPosition(clamp(initialPosition))
  }, [initialPosition])

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    let next: number | null = null
    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        next = sliderPosition - KEYBOARD_STEP
        break
      case 'ArrowRight':
      case 'ArrowUp':
        next = sliderPosition + KEYBOARD_STEP
        break
      case 'PageDown':
        next = sliderPosition - KEYBOARD_PAGE_STEP
        break
      case 'PageUp':
        next = sliderPosition + KEYBOARD_PAGE_STEP
        break
      case 'Home':
        next = 0
        break
      case 'End':
        next = 100
        break
      default:
        return
    }
    e.preventDefault()
    setSliderPosition(clamp(next))
  }, [sliderPosition])

  return (
    <Box
      ref={containerRef}
      onMouseDown={handleOnlyDrag ? undefined : handleMouseDown}
      onTouchStart={handleOnlyDrag ? undefined : handleTouchStart}
      onPointerMove={followPointer ? handlePointerFollow : undefined}
      onPointerLeave={followPointer ? handlePointerLeave : undefined}
      sx={[
        {
          position: 'relative',
          width: '100%',
          height,
          overflow: 'hidden',
          cursor: handleOnlyDrag ? 'default' : 'ew-resize',
          userSelect: 'none'
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : [])
      ]}
    >
      {/* After image (full width, behind) */}
      <Box
        component="img"
        src={afterImage}
        alt={afterAlt}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover'
        }}
      />

      {/* Before image (clipped to slider position) */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`,
          transition: jumpTransition
        }}
      >
        <Box
          component="img"
          src={beforeImage}
          alt={beforeAlt}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />
      </Box>

      {/* Slider divider line */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: `${sliderPosition}%`,
          transform: 'translateX(-50%)',
          width: styles.dividerWidth,
          height: '100%',
          backgroundColor: WHITE,
          zIndex: 2,
          pointerEvents: 'none',
          transition: jumpTransition
        }}
      />

      {/* Slider handle — with handleOnlyDrag, dragging is scoped here so clicks on the
          surrounding image area bubble up to parent handlers (e.g. card-open). */}
      <Box
        role="slider"
        tabIndex={0}
        aria-label={handleAriaLabel}
        aria-orientation="horizontal"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(sliderPosition)}
        onKeyDown={handleKeyDown}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e) }}
        onTouchStart={(e) => { e.stopPropagation(); handleTouchStart(e) }}
        sx={{
          position: 'absolute',
          top: '50%',
          left: `${sliderPosition}%`,
          transform: 'translate(-50%, -50%)',
          width: styles.handleSize,
          height: styles.handleSize,
          borderRadius: '50%',
          backgroundColor: WHITE,
          border: `1px solid ${BEIGE_600}`,
          boxShadow: '0px 4px 16px 0px rgba(19, 24, 29, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'ew-resize',
          zIndex: 3,
          transition: jumpTransition,
          '&:hover': {
            boxShadow: '0px 4px 16px 0px rgba(19, 24, 29, 0.2)'
          },
          '&:focus-visible': {
            outline: `2px solid ${BEIGE_600}`,
            outlineOffset: '2px',
            boxShadow: '0px 4px 16px 0px rgba(19, 24, 29, 0.2)'
          }
        }}
      >
        <ChevronLeftIcon sx={{ fontSize: styles.iconSize, marginRight: `-${styles.iconOffset}` }} />
        <ChevronRightIcon sx={{ fontSize: styles.iconSize, marginLeft: `-${styles.iconOffset}` }} />
      </Box>
    </Box>
  )
}
